import {Skeleton} from '@dropbox/dig-components/skeleton';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {growthbookCacheAtom} from 'atoms/growthbook';
import type {Timeframe} from 'client';
import {ProductRoadmapService} from 'client/services/ProductRoadmapService';
import {Link} from 'components/DSYS/Link';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {NotFound} from 'pages/NotFound';
import {Trans} from 'react-i18next';
import {getService} from 'utilities';
import {useRoadmapSearchParams} from 'views/roadmap/roadmapSearchParams';
import {RoadmapViewTitle} from 'views/roadmap/RoadmapViewTitle';
import {useTimeframesQuery} from 'views/workstreams/detail/hooks';
import {
  formatWorkstreamTimeframeLabel,
  getDefaultWorkstreamTimeframe,
  getTimeframeByName,
} from 'views/workstreams/detail/workstreamTimeframe';

import styles from './ProductRoadmap.module.css';
import {RoadmapDocumentEmbed} from './RoadmapDocumentEmbed';
import {RoadmapEmbedLoadingFrame} from './RoadmapEmbedLoadingFrame';

const getProductRoadmapTimeframe = (
  requestedTimeframe: string | null,
  timeframes: readonly Timeframe[]
) =>
  getTimeframeByName(
    requestedTimeframe
      ? requestedTimeframe.trim().replace(/[-_]+/g, ' ').toUpperCase()
      : getDefaultWorkstreamTimeframe(new Date(), timeframes),
    timeframes
  );

const ProductRoadmapEmbedError = () => (
  <Box className={styles.productRoadmapState} role="status">
    <Text isBold>{t('product_roadmap_embed_error_title')}</Text>
    <Text color="subtle">{t('we_are_on_it')}</Text>
  </Box>
);

const ProductRoadmapEmbedLoadingShell = () => (
  <Box className={styles.productRoadmapEmbedCard} aria-hidden="true">
    <Box className={styles.productRoadmapEmbedHeader}>
      <Skeleton.Text width={280} />
      <Skeleton.Text width={120} />
    </Box>
    <Box className={styles.productRoadmapEmbedFrameShell}>
      <RoadmapEmbedLoadingFrame />
    </Box>
  </Box>
);

const ProductRoadmapEmbed = ({
  timeframe,
  isTimeframesLoading,
  isTimeframesError,
}: {
  timeframe: Timeframe | undefined;
  isTimeframesLoading: boolean;
  isTimeframesError: boolean;
}) => {
  const {data, isError: isRoadmapError} = useQuery({
    queryKey: ['roadmap', 'product-embed-config', timeframe?.id ?? null],
    enabled: Boolean(timeframe?.id),
    queryFn: () =>
      getService(
        ProductRoadmapService
      ).getProductRoadmapEmbedConfigApiV1ProductRoadmapEmbedConfigGet(timeframe!.id),
  });

  const hasTimeframeLookupFinished = isTimeframesError || !isTimeframesLoading;
  const isTimeframeUnavailable = !timeframe && hasTimeframeLookupFinished;

  if (isRoadmapError || isTimeframeUnavailable) {
    return <ProductRoadmapEmbedError />;
  }

  if (!data) {
    return <ProductRoadmapEmbedLoadingShell />;
  }

  return (
    <>
      <Box className={styles.productRoadmapEmbedCard}>
        <Box className={styles.productRoadmapEmbedHeader}>
          <Text isBold>{data.title}</Text>
          <Text color="subtle">{new URL(data.open_url).hostname}</Text>
        </Box>
        <RoadmapDocumentEmbed data={data} />
      </Box>
      <Text className={styles.productRoadmapEmbedLink}>
        <Link to={data.open_url}>{t('product_roadmap_open_document')}</Link>
      </Text>
    </>
  );
};

const ProductRoadmapPage = () => {
  useDocumentTitle(t('product_roadmap_document_title'));
  const [searchParams] = useRoadmapSearchParams();
  const {timeframes, isLoading, isError} = useTimeframesQuery();
  const selectedTimeframe = getProductRoadmapTimeframe(searchParams.get('timeframe'), timeframes);

  return (
    <Box className={styles.page}>
      <Box className={styles.main}>
        <Box className={styles.header} data-testid="product-roadmap-header">
          <Box className={styles.headerTopRow}>
            <RoadmapViewTitle selectedView="product" />
          </Box>
        </Box>
        <Box className={styles.productRoadmapContent}>
          <Stack gap="24">
            <Stack gap="8">
              <Text tagName="h2" isBold>
                {t('product_roadmap_purpose_title')}
              </Text>
              <Text tagName="p">{t('product_roadmap_purpose_body')}</Text>
            </Stack>

            <SystemBanner
              tone="warning"
              role="status"
              withTitle={t('product_roadmap_internal_note_title')}
            >
              <Stack gap="8">
                <Text tagName="p">
                  <Trans
                    i18nKey="product_roadmap_internal_note_body"
                    t={t}
                    values={{
                      timeframe: formatWorkstreamTimeframeLabel(selectedTimeframe?.name ?? ''),
                    }}
                    components={{strong: <Text isBold />}}
                  />
                </Text>
                <Text tagName="p">{t('product_roadmap_external_convo_note_body')}</Text>
              </Stack>
            </SystemBanner>
            <ProductRoadmapEmbed
              timeframe={selectedTimeframe}
              isTimeframesLoading={isLoading}
              isTimeframesError={isError}
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export const ProductRoadmap = () => {
  const {isProductRoadmapEnabled} = useAtomValue(growthbookCacheAtom);
  return isProductRoadmapEnabled ? <ProductRoadmapPage /> : <NotFound />;
};
